import React, { useEffect, useState } from "react";

import moment from "moment";

import { cx, formatDate, useGetCropMixComposerScoreFactors, useLocation, useNavigate, useTranslation } from "utils";

import { CMC_MIX_TYPE, CMC_STEP_4_TOGGLE_OPTIONS, GOAL_LEVEL_2 } from "models";

import { Flex, Icon, KlimRadioGroup, PageSpin, Svg, Tooltip, Typography } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { CMC_ROUTES } from "pages/app/crop-mix-composer/CropMixComposerOverlay";
import { AutoMixLoadingScreen } from "pages/app/crop-mix-composer/components/AutoMixLoadingScreen";
import { MixSectionHeader } from "pages/app/crop-mix-composer/components/MixSectionHeader";
import { CropsOverviewTable } from "pages/app/crop-mix-composer/components/compositionStep/CropsOverviewTable";
import { ScoreSidePanel } from "pages/app/crop-mix-composer/components/compositionStep/ScoreSidePanel";
import { CropMixPieChart } from "pages/app/crop-mix-composer/components/overviewStep/CropMixPieChart";
import { CropMixSummaryTable } from "pages/app/crop-mix-composer/components/overviewStep/CropMixSummaryTable";
import { calculateAggregateMatchScore, generateGoalScores } from "pages/app/crop-mix-composer/utils/cropMixComposer";
import { useActiveCropMixSettings } from "pages/app/crop-mix-composer/utils/useActiveCropMixSettings";

export type GoalToScoreMap = Record<GOAL_LEVEL_2, number>;

export const MixOverview = ({
  fieldId,
  isRegeneratingSmartMix,
  onRegenerateSmartMix,
}: {
  fieldId?: string;
  isRegeneratingSmartMix: boolean;
  onRegenerateSmartMix: () => void;
}) => {
  const { t } = useTranslation("cropMixComposer");
  const navigate = useNavigate();
  const location = useLocation();

  const { selectedCrops, isPageLoading, activeCropMixId, selectedGoals, setAggregateGoalScores, setAggregateMatchScore, weightShareDistribution } =
    useCropMixComposerContext();

  const { data: scoreFactors } = useGetCropMixComposerScoreFactors();
  const activeCropMixSettings = useActiveCropMixSettings();

  const [selectedToggleOption, setSelectedToggleOption] = useState<CMC_STEP_4_TOGGLE_OPTIONS>(CMC_STEP_4_TOGGLE_OPTIONS.SEED_SHARE);
  const [isPreparingPrint, setIsPreparingPrint] = useState(false);

  const printDate = formatDate(new Date().toISOString(), "LL", moment.ISO_8601);
  const printTitle = [activeCropMixSettings?.name, printDate].filter(Boolean).join(" - ");

  useEffect(() => {
    if (!scoreFactors) return;

    const aggregateGoalScores = generateGoalScores(selectedGoals, weightShareDistribution, scoreFactors, selectedCrops);
    setAggregateGoalScores(aggregateGoalScores);
    const aggregateMatchScore = calculateAggregateMatchScore(selectedCrops, weightShareDistribution);
    setAggregateMatchScore(aggregateMatchScore);
  }, [scoreFactors, selectedCrops, selectedGoals, setAggregateGoalScores, setAggregateMatchScore, weightShareDistribution]);

  // Printing the rebuilt table (see CropsOverviewTable) has to wait for a paint: `Icon` resolves its
  // SVG through a dynamic import, so printing in the same task loses the cost symbols. The document
  // title is what the browser offers as the file name.
  useEffect(() => {
    if (!isPreparingPrint) return undefined;

    const pageTitle = document.title;
    document.title = printTitle || pageTitle;

    const stopPreparing = () => {
      document.title = pageTitle;
      setIsPreparingPrint(false);
    };

    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => window.print());
    });

    window.addEventListener("afterprint", stopPreparing, { once: true });

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("afterprint", stopPreparing);
      document.title = pageTitle;
    };
  }, [isPreparingPrint, printTitle]);

  if (isPageLoading && !isRegeneratingSmartMix) return <PageSpin />;

  const toggleOptions = [
    { label: t(`page.${CMC_ROUTES.OVERVIEW}.pieChartToggle.seedShare`), value: CMC_STEP_4_TOGGLE_OPTIONS.SEED_SHARE },
    { label: t(`page.${CMC_ROUTES.OVERVIEW}.pieChartToggle.weightShare`), value: CMC_STEP_4_TOGGLE_OPTIONS.WEIGHT_SHARE },
  ];

  const basePath = location.pathname.split("cmc")[0];
  const mixType = activeCropMixSettings?.mixType;
  const isMixDivergedFromSmartMix = mixType === CMC_MIX_TYPE.MANUAL || mixType === CMC_MIX_TYPE.AUTO_GENERATED_EDITED;
  const canRegenerateSmartMix = isMixDivergedFromSmartMix && !isRegeneratingSmartMix;

  return (
    <Flex className="crop-mix-composer-overlay-step-mix-details" data-testid="crop-mix-composer-overlay-mix-details">
      <div className="cmc-print-header">
        <Svg svgName="logos/klim_logo_text_black" className="cmc-print-header-logo" />
        <Typography variant="paragraph-s" color="grey-600" className="cmc-print-header-caption margin-0">
          {[activeCropMixSettings?.name, printDate].filter(Boolean).join(" · ")}
        </Typography>
      </div>
      <Flex className="mix-details-section" vertical gap="2rem" justify={isRegeneratingSmartMix ? "center" : undefined}>
        {isRegeneratingSmartMix ? (
          <AutoMixLoadingScreen />
        ) : (
          <>
            <MixSectionHeader
              title={t(`page.${CMC_ROUTES.OVERVIEW}.mixOverview`)}
              subTitle={t(`page.${CMC_ROUTES.OVERVIEW}.curatedMix`)}
              actions={
                <>
                  <Tooltip placement="top" title={t(`page.${CMC_ROUTES.OVERVIEW}.downloadPdfIcon`)}>
                    <Icon
                      className="download-icon"
                      dataTestId="mix-overview-download-pdf"
                      iconName="tabler/download"
                      onClick={() => setIsPreparingPrint(true)}
                    />
                  </Tooltip>
                  <Tooltip placement="top" title={t(`page.${CMC_ROUTES.OVERVIEW}.regenerateSmartMixIcon`)}>
                    <Icon
                      className={`regenerate-icon ${canRegenerateSmartMix ? "" : "disabled"}`}
                      iconName="tabler/arrow-back-up"
                      onClick={canRegenerateSmartMix ? onRegenerateSmartMix : undefined}
                    />
                  </Tooltip>
                  <Tooltip placement="top" title={t(`page.${CMC_ROUTES.OVERVIEW}.editMixIcon`)}>
                    <Icon
                      className="edit-icon"
                      iconName="tabler/edit"
                      onClick={() => {
                        navigate(`${basePath}cmc/${activeCropMixId}/${CMC_ROUTES.COMPOSITION}${location.search}`);
                      }}
                    />
                  </Tooltip>
                </>
              }
            />
            <Flex className="mix-details-section-mix-chart" vertical gap="1rem">
              <KlimRadioGroup
                type="other"
                size="middle"
                dataTestId="mix-details-result-toggle"
                className="mix-details-result-toggle"
                options={toggleOptions}
                onChange={(e) => setSelectedToggleOption(e.target.value)}
                value={selectedToggleOption}
                optionType="button"
                buttonStyle="outline"
              />
              <Flex className="mix-details-container" gap="2rem">
                <Flex className="mix-pie-charts" gap="2rem">
                  {toggleOptions.map(({ label, value }) => (
                    <Flex key={value} className={cx("mix-pie-chart", value !== selectedToggleOption && "cmc-print-only-chart")} vertical gap="0.5rem">
                      <Typography variant="paragraph-s" bold className="cmc-print-chart-title margin-0">
                        {label}
                      </Typography>
                      <CropMixPieChart selectedToggleOption={value} />
                    </Flex>
                  ))}
                </Flex>
                <CropMixSummaryTable selectedCrops={selectedCrops} />
              </Flex>
            </Flex>
            <Flex className="mix-details-section-table" vertical>
              <Typography variant="paragraph-m" bold className="table-title">
                {t(`page.${CMC_ROUTES.OVERVIEW}.mixDetails`)}
              </Typography>
              <CropsOverviewTable cropMixComposerResponse={selectedCrops} isPreparingPrint={isPreparingPrint} />
            </Flex>
          </>
        )}
      </Flex>
      <ScoreSidePanel fieldId={fieldId} />
    </Flex>
  );
};
